import React, { useRef, type CSSProperties } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import { PanelSplitter, PanelSplitterProvider } from '@atlaskit/navigation-system';
import { useIntl } from '@atlassian/jira-intl';
import { getResizeBounds } from '../../common/utils.tsx';
import {
	OBJECT_GRAPH_SIDE_PANEL_MAX_WIDTH,
	OBJECT_GRAPH_SIDE_PANEL_MIN_WIDTH,
} from '../../common/constants.tsx';
import messages from './messages.tsx';

const resizingCssVar = '--panel-splitter-resizing';
const widthVar = '--panel-width';

type GraphPanelSplitterProps = {
	children?: React.ReactNode;
	isOpen: boolean;
	sidebarWidth: number;
	setSidebarWidth: (newWidth: number) => void;
};

export const GraphPanelSplitter = ({
	isOpen,
	sidebarWidth,
	setSidebarWidth,
	children,
}: GraphPanelSplitterProps) => {
	const panelSplitterParentRef = useRef<HTMLDivElement | null>(null);
	const { formatMessage } = useIntl();

	const setAndStoreWidth = (newWidth: number) => {
		const validNewWidth = Math.min(
			OBJECT_GRAPH_SIDE_PANEL_MAX_WIDTH,
			Math.max(OBJECT_GRAPH_SIDE_PANEL_MIN_WIDTH, newWidth),
		);
		setSidebarWidth(validNewWidth);
	};

	if (!isOpen) return null;

	return (
		<Box xcss={sidebarPanelWrapper}>
			<Box
				ref={panelSplitterParentRef}
				xcss={resizableStyles}
				// eslint-disable-next-line jira/react/no-style-attribute -- Ignored via go/DSP-18766
				style={
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					{
						[widthVar]: `${sidebarWidth}px`,
					} as CSSProperties
				}
				testId="assets-graph-panel-splitter.ui.graph-panel-splitter.box"
			>
				<PanelSplitterProvider
					panelRef={panelSplitterParentRef}
					panelWidth={sidebarWidth}
					onCompleteResize={setAndStoreWidth}
					getResizeBounds={getResizeBounds}
					resizingCssVar={resizingCssVar}
					position="start"
				>
					<Box xcss={objectViewWrapperStyles}>{children}</Box>
					<PanelSplitter label={formatMessage(messages.graphPanelSplitterLabel)} />
				</PanelSplitterProvider>
			</Box>
		</Box>
	);
};

const sidebarPanelWrapper = xcss({
	overflow: 'hidden',
	paddingLeft: 'space.025',
});

const objectViewWrapperStyles = xcss({
	height: '100%',
	overflow: 'hidden',
	borderLeftWidth: '1px',
	borderLeftStyle: 'solid',
	borderLeftColor: 'color.border',
});

const resizableStyles = xcss({
	position: 'relative',
	backgroundColor: 'elevation.surface',
	width: `var(${resizingCssVar}, var(${widthVar}))`,
	height: '100%',
});
